import React, { Component } from 'react';
import { Button, Form, Alert, Card } from 'react-bootstrap';
import { GoX, GoPlay } from 'react-icons/go';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/theme-monokai';
import Select from 'components/controls/select';
import SectionLabel from 'components/controls/sectionLabel';
import StatusPill from '../../components/statusPill';
import MetaThread from './metaThread';
import WorkflowGraph from './workflowGraph';
import ActionCard, { ActionCards } from '../../../../../controls/common/actionCard';
import { upsertCell } from '../../domain/model';
import { WORKSPACE_MODES, CELL_WORKFLOW_MODES } from '../../domain/schema';
import { isStructureEditable } from './cellRules';

/**
 * CellPanel — right-docked detail panel for a selected cell.
 *
 * Layout: one "Cell" section (`Card.Text as="h6"` + `<hr/>`) with a single-row Status properties table,
 *   then SectionLabel subsections — Details (title + description), Cell Workflow (cell_workflow_mode +
 *   the specify-mode workflow / mini-graph), and Workspace (workspace_mode + the specify-mode workspace)
 *   — followed by the Actions section and the Meta-thread timeline. Structural pickers are editable while
 *   isStructureEditable(cell) is true and read-only + a "locked — cell has started" hint once the cell
 *   has run (see cellRules.js).
 *
 * Cell Workflow and Workspace each carry a MODE (cell_workflow_mode / workspace_mode) with the same
 *   choices — inherit_from_mission · specify_* · inherit_from_previous_cell. Only the "specify" mode shows
 *   a second Select (a workflow / workspace to pick); both inherit modes are resolved at RUNTIME by the
 *   orchestrator, so picking one writes the mode with the value unresolved (null) and shows no second
 *   picker. New cells default both modes to inherit_from_mission.
 *
 * Actions — the applicable actions for the cell's status, one compact card each; hidden when none apply:
 *   draft → Activate · input_needed → Answer Input (or Approve / Request Changes for a review gate) ·
 *   failed → Retry · ready|working → Pause · inactive → Resume.
 *
 * Details (title + description) are editable only while status === "draft", read-only otherwise.
 *
 * Every write here is INCREMENTAL: it calls the client then applies a pure store reducer via
 * onApplyLocal — never a full reload. The panel stays open and the mission graph updates in place.
 * cell_workflow / workspace / workspace_mode freeze the first time the cell reaches "working".
 *
 * Props:
 *   cell         — cell vertex object from the store
 *   missionId    — string
 *   client       — SystemOfWorkClient instance
 *   onClose      — () => void
 *   onApplyLocal — (updater: store => store) => void — incremental local store update
 *   templates    — store.templates ({ workflows, workspaces } palettes)
 */

// Human-readable labels for WORKSPACE_MODES / CELL_WORKFLOW_MODES (schema.js keeps the wire values snake_case).
const WORKSPACE_MODE_LABELS = {
  inherit_from_mission: 'Inherit from mission',
  specify_workspace: 'Specify workspace',
  inherit_from_previous_cell: 'Inherit from previous cell',
};
const CELL_WORKFLOW_MODE_LABELS = {
  inherit_from_mission: 'Inherit from mission',
  specify_workflow: 'Specify workflow',
  inherit_from_previous_cell: 'Inherit from previous cell',
};

// Leading "clear" option for the workspace / cell-workflow name pickers (value '' → save clears to null).
const NONE_OPTION = { value: '', label: '— none —' };

// ---------------------------------------------------------------------------
// ActionZone — the "Actions" section: one compact card per applicable action
// ---------------------------------------------------------------------------

// ActionZone renders the whole "Actions" section (heading + hr + cards) for the cell's current status,
// or null when the status has no applicable action (planned/completed) — so the section is hidden
// entirely, heading and separator included. Each action is its own compact card; every word of every
// button label is capitalized. The free-form input case keeps its options + textarea because the
// underlying answerInput call needs the typed reply. Behavior is unchanged: each handler calls the
// client then onActionDone / onLocalStatus. ready/working expose a Pause card and inactive a Resume
// card, both writing status via client.setCellProps (the same graph-mutation path Activate uses).
function ActionZone({ cell, missionId, client, onActionDone, onLocalStatus }) {
  const props = cell.props || {};
  const status = props.status || 'draft';
  const [answer, setAnswer] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState(null);

  const runAsync = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      if (onActionDone) onActionDone();
    } catch (e) {
      setActionError(e && e.message ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  // Activate / retry both move the cell to "planned"; reflect that locally so the
  // panel and graph update in place without a reload.
  const activate = () => runAsync(async () => {
    await client.activateCell(missionId, cell.element_id);
    if (onLocalStatus) onLocalStatus('planned');
  });

  // Pause / resume write "inactive" / "ready" directly via setCellProps (the same graph-mutation path
  // Activate uses) — there is no dedicated client method for these, unlike activateCell.
  const pause = () => runAsync(async () => {
    await client.setCellProps(missionId, cell.element_id, { status: 'inactive' });
    if (onLocalStatus) onLocalStatus('inactive');
  });

  const resume = () => runAsync(async () => {
    await client.setCellProps(missionId, cell.element_id, { status: 'ready' });
    if (onLocalStatus) onLocalStatus('ready');
  });

  // Build the applicable action card(s) for this status. An empty list ⇒ the whole section is hidden.
  // isActionCardList is false only for the free-form input case, which renders its own rich Card
  // instead of a plain ActionCard row, so it isn't wrapped in the shared <ActionCards> frame.
  let cards = [];
  let isActionCardList = true;

  if (status === 'draft') {
    cards = [
      <ActionCard key="activate" helper="Wire dependencies, then activate this cell">
        <Button variant="success" size="sm" disabled={busy} onClick={activate}>
          <GoPlay /> {busy ? 'Activating…' : 'Activate Cell'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'failed') {
    cards = [
      <ActionCard key="retry" helper="A step errored — see the meta-thread for details">
        <Button variant="success" size="sm" disabled={busy} onClick={activate}>
          {busy ? 'Retrying…' : 'Retry'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'input_needed') {
    const inputMeta = props.input_needed || {};
    const kind = inputMeta.kind || 'question';

    if (kind === 'review') {
      // A review gate exposes two actions — approve, or request changes — so it splits into two cards.
      cards = [
        <ActionCard key="approve" helper="Approve the change">
          <Button
            variant="success"
            size="sm"
            disabled={busy}
            onClick={() => runAsync(() => client.answerInput(missionId, cell.element_id, 'approved'))}
          >
            {busy ? '…' : 'Approve'}
          </Button>
        </ActionCard>,
        <ActionCard key="changes" helper="Request changes — loops back to implement">
          <Button
            variant="danger"
            size="sm"
            disabled={busy}
            onClick={() => runAsync(() => client.answerInput(missionId, cell.element_id, 'changes'))}
          >
            {busy ? '…' : 'Request Changes'}
          </Button>
        </ActionCard>,
      ];
    } else {
      const mode = inputMeta.mode || 'in_flight';
      const question = inputMeta.question || inputMeta.q || 'Input required.';
      const opts = inputMeta.opts || inputMeta.options || [];

      // Free-form / choice input keeps its options + textarea (answerInput needs the reply); the submit
      // button sits beside a muted hint to match the compact card idiom.
      isActionCardList = false;
      cards = [
        <Card className="mb-2" key="answer">
          <Card.Body>
            <p className="text-muted mb-1" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              needs input · <code>{kind}</code> · {mode === 'in_flight' ? 'in-flight — resumes this step' : 'gate'}
            </p>
            <p className="mb-2" style={{ fontSize: '0.88rem', fontWeight: 600 }}>{question}</p>
            {opts.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {opts.map((o, i) => (
                  <Button
                    key={i}
                    variant={answer === o ? 'success' : 'light'}
                    size="sm"
                    onClick={() => setAnswer(o)}
                  >
                    {o}
                  </Button>
                ))}
              </div>
            )}
            <Form.Control
              as="textarea"
              size="sm"
              rows={2}
              placeholder="Reply or add detail…"
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <div className="d-flex align-items-center justify-content-between" style={{ gap: 12 }}>
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>Send your reply to resume the cell.</span>
              <Button
                variant="success"
                size="sm"
                disabled={busy || !answer.trim()}
                onClick={() => runAsync(() => client.answerInput(missionId, cell.element_id, answer.trim()))}
                style={{ flexShrink: 0 }}
              >
                {busy ? 'Sending…' : 'Answer Input'}
              </Button>
            </div>
          </Card.Body>
        </Card>,
      ];
    }
  } else if (status === 'ready' || status === 'working') {
    cards = [
      <ActionCard key="pause" helper="Pause — the heartbeat will stop advancing this cell">
        <Button variant="warning" size="sm" disabled={busy} onClick={pause}>
          {busy ? 'Pausing…' : 'Pause'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'inactive') {
    cards = [
      <ActionCard key="resume" helper="Resume from where it paused">
        <Button variant="success" size="sm" disabled={busy} onClick={resume}>
          {busy ? 'Resuming…' : 'Resume'}
        </Button>
      </ActionCard>,
    ];
  }

  if (cards.length === 0) return null;

  return (
    <>
      <Card.Text as="h6">Actions</Card.Text>
      {isActionCardList ? <ActionCards>{cards}</ActionCards> : cards}
      {actionError && (
        <Alert variant="warning" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>{actionError}</Alert>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// CellPanel — main panel component
// ---------------------------------------------------------------------------

class CellPanel extends Component {
  constructor(props) {
    super(props);
    const cellProps = (props.cell && props.cell.props) || {};
    this._titleRef = React.createRef();
    this.state = {
      metaItems: [],
      metaLoading: false,
      titleDraft: cellProps.title || '',
      descriptionDraft: cellProps.description || '',
    };
  }

  componentDidMount() {
    this._mounted = true;
    this._loadMetaThread();
    this._autoGrowTitle();
  }

  componentDidUpdate(prevProps) {
    // Re-fetch meta-thread and re-seed editable drafts only when the selected cell's identity changes
    if ((prevProps.cell && prevProps.cell.element_id) !== (this.props.cell && this.props.cell.element_id)) {
      this._loadMetaThread();
      this._seedDrafts();
    }
    // Keep the title field sized to its content (covers seeding + draft-mode toggles).
    this._autoGrowTitle();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async _loadMetaThread() {
    const { cell, client, missionId } = this.props;
    if (!cell || !client) return;

    this.setState({ metaLoading: true });
    try {
      const items = await client.listMetaThread(missionId, cell.element_id);
      if (!this._mounted) return;
      this.setState({ metaItems: items || [], metaLoading: false });
    } catch (_) {
      if (!this._mounted) return;
      this.setState({ metaItems: [], metaLoading: false });
    }
  }

  // Seed the editable title/description drafts from the current cell props.
  _seedDrafts() {
    const cellProps = (this.props.cell && this.props.cell.props) || {};
    this.setState({
      titleDraft: cellProps.title || '',
      descriptionDraft: cellProps.description || '',
    });
  }

  // Merge a prop change into the store locally (no reload) so the graph + panel
  // reflect it immediately.
  _applyLocalProps(changed) {
    const { cell, missionId, onApplyLocal } = this.props;
    if (!onApplyLocal || !cell) return;
    onApplyLocal(s => upsertCell(s, missionId, {
      ...cell,
      props: { ...(cell.props || {}), ...changed },
    }));
  }

  async _saveTitle() {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    const next = this.state.titleDraft;
    const current = (cell.props && cell.props.title) || '';
    if (next === current) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { title: next });
      this._applyLocalProps({ title: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveDescription() {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    const next = this.state.descriptionDraft;
    const current = (cell.props && cell.props.description) || '';
    if (next === current) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { description: next });
      this._applyLocalProps({ description: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Persist the draft cell's selected workflow. Mirrors _saveTitle/_saveDescription:
  // skip when unchanged, write via setCellProps, then reflect locally. The empty
  // option clears the assignment (null). cell_workflow is the canonical server key
  // (see client.addCell), so it is used on the wire and locally alike.
  async _saveCellWorkflow(value) {
    const { cell } = this.props;
    const selected = value || '';
    const current = (cell && cell.props && cell.props.cell_workflow) || '';
    if (selected === current) return;
    // The workflow-name Select only shows in specify_workflow mode, so keep the mode alongside the name.
    await this._saveCellWorkflowFields('specify_workflow', selected || null);
  }

  // Shared write path for the cell_workflow_mode + cell_workflow pair (they travel together, like the
  // workspace pair). Mirrors _saveWorkspaceFields.
  async _saveCellWorkflowFields(mode, cellWorkflow) {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { cell_workflow_mode: mode, cell_workflow: cellWorkflow });
      this._applyLocalProps({ cell_workflow_mode: mode, cell_workflow: cellWorkflow });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // cell_workflow_mode Select onChange. Only specify_workflow keeps a workflow; the inherit modes clear
  // it (resolved at runtime), mirroring _saveWorkspaceMode.
  async _saveCellWorkflowMode(mode) {
    const { cell, client } = this.props;
    if (!cell || !client) return;
    const next = mode || '';
    const current = (cell.props && cell.props.cell_workflow_mode) || '';
    if (next === current) return;
    const cellWorkflow = next === 'specify_workflow' ? ((cell.props && cell.props.cell_workflow) || null) : null;
    await this._saveCellWorkflowFields(next || null, cellWorkflow);
  }

  // Shared write path for the workspace_mode + workspace pair (they always travel together — a mode
  // change without its resolved workspace, or vice versa, would leave the two inconsistent server-side).
  // Mirrors _saveCellWorkflow's write-then-reflect-locally shape.
  async _saveWorkspaceFields(mode, workspace) {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { workspace_mode: mode, workspace });
      this._applyLocalProps({ workspace_mode: mode, workspace });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // workspace_mode Select onChange. Only "specify_workspace" resolves a workspace at pick time (the name
  // Select below does that). BOTH inherit modes are resolved at RUNTIME by the orchestrator, so we write
  // the mode with workspace unresolved (null):
  //  - specify_workspace          — workspace stays null here; the name Select resolves it on pick.
  //  - inherit_from_mission       — resolved from the mission's default workspace at runtime.
  //  - inherit_from_previous_cell — resolved from the previous cell at runtime.
  async _saveWorkspaceMode(mode) {
    const { cell, client } = this.props;
    if (!cell || !client) return;
    const next = mode || '';
    const current = (cell.props && cell.props.workspace_mode) || '';
    if (next === current) return;
    await this._saveWorkspaceFields(next || null, null);
  }

  // Workspace-name Select onChange (specify_workspace mode only). Copies the chosen palette entry's
  // { type, work_folder } as an embedded snapshot — never a reference by name (see schema.js: workspaces
  // are ACTUAL, not templates).
  async _saveWorkspace(name) {
    const { cell, client, templates } = this.props;
    if (!cell || !client) return;
    const workspaces = (templates && templates.workspaces) || [];
    const currentWorkspace = (cell.props && cell.props.workspace) || null;
    const currentEntry = currentWorkspace
      ? workspaces.find(w => w.work_folder === currentWorkspace.work_folder)
      : null;
    const currentName = (currentEntry && currentEntry.name) || '';
    const selected = name || '';
    if (selected === currentName) return;
    const entry = selected ? workspaces.find(w => w.name === selected) : null;
    const workspace = entry ? { type: entry.type, work_folder: entry.work_folder } : null;
    const mode = (cell.props && cell.props.workspace_mode) || 'specify_workspace';
    await this._saveWorkspaceFields(mode, workspace);
  }

  // Grow the title textarea to fit its content (no inner scrollbar). Accepts the
  // live event target during typing, otherwise falls back to the mounted ref.
  _autoGrowTitle(el) {
    const node = el || this._titleRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }

  render() {
    const { cell, missionId, client, onClose, templates } = this.props;
    const { metaItems, metaLoading, titleDraft, descriptionDraft } = this.state;

    if (!cell) return null;

    const props = cell.props || {};
    const status = props.status || 'draft';
    const title = props.title || cell.element_id;
    const description = props.description || '';
    const cellWorkflowKey = props.cell_workflow || '';
    // Effective cell-workflow mode. Older cells predate cell_workflow_mode: treat a bare cell_workflow
    // as specify_workflow, otherwise unset.
    const cellWorkflowMode = props.cell_workflow_mode || (cellWorkflowKey ? 'specify_workflow' : null);
    const workspace = props.workspace || null;
    const workspaceMode = props.workspace_mode || null;
    const currentStep = props.current_step || null;

    // Structural props (cell_workflow / workspace / *_mode) freeze the first time the cell reaches
    // "working" — independent of the title/description draft-only rule below.
    const structureEditable = isStructureEditable(cell);

    // Resolve workflow template (workflows is a LIST of { name, ... })
    const workflows = (templates && templates.workflows) || [];
    const workflow = cellWorkflowKey && Array.isArray(workflows)
      ? (workflows.find(w => w.name === cellWorkflowKey) || null)
      : null;

    // cell_workflow_mode picker options + the specify_workflow name picker (no "— none —": a workflow is
    // chosen only in specify mode).
    const cellWorkflowModeOptions = CELL_WORKFLOW_MODES.map(m => ({ value: m, label: CELL_WORKFLOW_MODE_LABELS[m] || m }));
    const cellWorkflowModeSelectValue = cellWorkflowModeOptions.find(o => o.value === cellWorkflowMode) || null;
    const workflowOptions = [
      NONE_OPTION,
      ...(Array.isArray(workflows) ? workflows.map(w => w.name).filter(Boolean).map(n => ({ value: n, label: n })) : []),
    ];
    const workflowSelectValue = workflowOptions.find(o => o.value === cellWorkflowKey) || NONE_OPTION;

    // workspace_mode picker options (WORKSPACE_MODES, human-labeled).
    const workspaceModeOptions = WORKSPACE_MODES.map(m => ({ value: m, label: WORKSPACE_MODE_LABELS[m] || m }));
    const workspaceModeSelectValue = workspaceModeOptions.find(o => o.value === workspaceMode) || null;

    // specify_workspace's palette picker (workspaces is a LIST of { name, type, work_folder }); the
    // current selection is reverse-matched by work_folder since the embedded copy on the cell doesn't
    // retain the palette entry's name.
    const workspaceTemplates = (templates && templates.workspaces) || [];
    const workspaceOptions = [
      NONE_OPTION,
      ...(Array.isArray(workspaceTemplates) ? workspaceTemplates.map(w => w.name).filter(Boolean).map(n => ({ value: n, label: n })) : []),
    ];
    const selectedWorkspaceEntry = workspace
      ? workspaceTemplates.find(w => w.work_folder === workspace.work_folder) || null
      : null;
    const workspaceSelectValue = (selectedWorkspaceEntry
      && workspaceOptions.find(o => o.value === selectedWorkspaceEntry.name)) || NONE_OPTION;

    return (
      <>
        {onClose && (
          <span className="workflow-controls-pane-right">
            <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
          </span>
        )}

        {/* Static panel header — the cell's own title lives in the Details section below */}
        <Card.Text as="h6">Cell</Card.Text>
        <hr />

        {/* ID + Status — the standard gray-chip properties table (value cells carry the .prop-value-column
            background). */}
        <Card.Text>
          <table className="properties">
            <tbody>
              <tr>
                <td className="prop-name-column">ID</td>
                <td className="prop-value-column"><code>{cell.element_id}</code></td>
              </tr>
              <tr>
                <td className="prop-name-column">Status</td>
                <td className="prop-value-column"><StatusPill status={status} /></td>
              </tr>
            </tbody>
          </table>
        </Card.Text>

        {/* Details — title + description, editable only while the cell is a draft, read-only afterwards */}
        <SectionLabel>Details</SectionLabel>
        {status === 'draft' ? (
          <>
            <Form.Group>
              <Form.Control
                ref={this._titleRef}
                as="textarea"
                rows={1}
                size="sm"
                value={titleDraft}
                placeholder="Cell title…"
                style={{ resize: 'none', overflow: 'hidden' }}
                onChange={e => { this.setState({ titleDraft: e.target.value }); this._autoGrowTitle(e.target); }}
                onBlur={() => this._saveTitle()}
              />
              <Form.Text muted>
                A short, descriptive name for this cell.
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <AceEditor
                mode="markdown"
                theme="monokai"
                width="100%"
                wrapEnabled
                minLines={4}
                maxLines={12}
                value={descriptionDraft}
                onChange={value => this.setState({ descriptionDraft: value })}
                onBlur={() => this._saveDescription()}
              />
              <Form.Text muted>
                Optional notes or context for this cell.
              </Form.Text>
            </Form.Group>
          </>
        ) : (
          <>
            <p className="font-weight-bold">{title}</p>
            <Card.Text>
              {description || <span className="text-muted">No description provided.</span>}
            </Card.Text>
          </>
        )}

        {/* Cell Workflow — mode picker; the workflow-name Select appears only in specify_workflow mode.
            Both inherit modes resolve at runtime (see _saveCellWorkflowMode). */}
        <SectionLabel>Cell Workflow</SectionLabel>
        <Form.Group>
          {structureEditable ? (
            <Select
              options={cellWorkflowModeOptions}
              value={cellWorkflowModeSelectValue}
              onChange={opt => this._saveCellWorkflowMode(opt ? opt.value : '')}
              menuPortalTarget={document.body}
              placeholder="Choose a mode…"
            />
          ) : (
            <div>
              {cellWorkflowMode
                ? (CELL_WORKFLOW_MODE_LABELS[cellWorkflowMode] || cellWorkflowMode)
                : <span className="text-muted">—</span>}
              <small className="text-muted d-block">locked — cell has started</small>
            </div>
          )}
          <Form.Text muted>
            How this cell gets its workflow. &quot;Specify workflow&quot; picks one now; the inherit modes
            resolve at runtime.
          </Form.Text>
        </Form.Group>
        {structureEditable ? (
          cellWorkflowMode === 'specify_workflow' && (
            <Form.Group>
              <Select
                options={workflowOptions}
                value={workflowSelectValue}
                onChange={opt => this._saveCellWorkflow(opt ? opt.value : '')}
                menuPortalTarget={document.body}
                placeholder="Choose a workflow…"
              />
              {/* Show the specified workflow's step graph right under the picker. */}
              {workflow && <WorkflowGraph workflow={workflow} />}
              <Form.Text muted>
                Associate this cell with a workflow procedure.
              </Form.Text>
            </Form.Group>
          )
        ) : workflow ? (
          <>
            <WorkflowGraph workflow={workflow} currentStep={currentStep} />
            <small className="text-muted d-block mb-3">Orchestrator interprets it; current step is highlighted.</small>
          </>
        ) : null}

        {/* Workspace — mode picker; the workspace-name Select appears only in specify_workspace mode.
            Both inherit modes resolve at runtime (see _saveWorkspaceMode). */}
        <SectionLabel>Workspace</SectionLabel>
        <Form.Group>
          {structureEditable ? (
            <Select
              options={workspaceModeOptions}
              value={workspaceModeSelectValue}
              onChange={opt => this._saveWorkspaceMode(opt ? opt.value : '')}
              menuPortalTarget={document.body}
              placeholder="Choose a mode…"
            />
          ) : (
            <div>
              {workspaceMode
                ? (WORKSPACE_MODE_LABELS[workspaceMode] || workspaceMode)
                : <span className="text-muted">—</span>}
              <small className="text-muted d-block">locked — cell has started</small>
            </div>
          )}
          <Form.Text muted>
            How this cell gets its workspace. &quot;Specify workspace&quot; picks one now; the inherit
            modes resolve at runtime.
          </Form.Text>
        </Form.Group>
        {structureEditable ? (
          workspaceMode === 'specify_workspace' && (
            <Form.Group>
              <Select
                options={workspaceOptions}
                value={workspaceSelectValue}
                onChange={opt => this._saveWorkspace(opt ? opt.value : '')}
                menuPortalTarget={document.body}
                placeholder="— none —"
              />
              {workspace && workspace.work_folder && (
                <Form.Text muted><code>{workspace.work_folder}</code></Form.Text>
              )}
            </Form.Group>
          )
        ) : workspace && workspace.work_folder ? (
          <Form.Group>
            <div><code>{workspace.work_folder}</code></div>
          </Form.Group>
        ) : null}

        {/* Actions — one compact card per applicable action; the whole section hides when none apply */}
        <ActionZone
          cell={cell}
          missionId={missionId}
          client={client}
          onActionDone={() => this._loadMetaThread()}
          onLocalStatus={(status) => this._applyLocalProps({ status })}
        />

        <Card.Text as="h6">Meta-thread</Card.Text>
        <hr />
        <MetaThread items={metaItems} loading={metaLoading} />
      </>
    );
  }
}

export default CellPanel;
